import { useEffect, useState } from 'react'
import config from '@/config'
import {
  Save,
  Globe,
  GlobeLock,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Inbox,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import countryHubService from '@/services/countryHubService'
import previousHubService from '@/services/previousHubService'
import RepeaterField from '@/components/RepeaterField'
import IconPicker from '@/components/IconPicker'
import ImageUploader from '@/components/ImageUploader'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import StickyActionBar from '@/components/common/StickyActionBar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@/components/ui/alert'
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { cn } from '@/lib/utils'

function emptyHub() {
  return {
    manager: {
      photoUrl: '',
      fullName: '',
      role: '',
      bio: '',
      yearsExperience: 0,
      certifications: [],
      linkedin: '',
      twitter: '',
    },
    organization: { name: '', tagline: '', logoUrl: '', foundedYear: null, website: '' },
    kpis: [],
    faq: [],
    support: {
      primaryEmail: '',
      supportPhone: '',
      whatsapp: '',
      responseTimeSla: '',
      officeHours: '',
      emergencyContact: '',
    },
    theme: { accentColor: '#B8924A', coverImageUrl: '' },
    footerCta: { headline: '', subheadline: '' },
    isPublished: false,
  }
}

const TABS = [
  { value: 'profile', label: 'Tu perfil' },
  { value: 'kpis', label: 'KPIs' },
  { value: 'faq', label: 'FAQ' },
  { value: 'support', label: 'Soporte' },
  { value: 'branding', label: 'Branding' },
  { value: 'footer', label: 'Footer CTA' },
]

function FieldGroup({ children, className }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', className)}>{children}</div>
  )
}

function Field({ label, htmlFor, helper, children, full = false }) {
  return (
    <div className={cn('space-y-1.5', full && 'sm:col-span-2')}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {helper && <p className="text-caption text-subtle">{helper}</p>}
    </div>
  )
}

export default function CountryHubEditor() {
  const { confirmar, dialogo } = useConfirmacion()
  const { partner } = useAuthStore()
  const countryCode = partner?.managedCountry || ''
  const publicUrl = `${config.portalUrl.replace(/\/+$/, '')}/${countryCode.toLowerCase()}`

  const [activeTab, setActiveTab] = useState('profile')
  const [hub, setHub] = useState(emptyHub())
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [error, setError] = useState(null)

  const [profiles, setProfiles] = useState([])
  const [profilesDialogOpen, setProfilesDialogOpen] = useState(false)
  const [isApplyingProfile, setIsApplyingProfile] = useState(false)

  const [previousArchive, setPreviousArchive] = useState(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importBannerDismissed, setImportBannerDismissed] = useState(false)
  const [importSelection, setImportSelection] = useState({
    faq: true,
    support: true,
    successStories: true,
    futureEvents: true,
    kpis: false,
    branding: false,
    footerCta: false,
    managerProfile: false,
    announcements: false,
  })
  const [isImporting, setIsImporting] = useState(false)

  useEffect(() => {
    load()
    checkPrevious()
    loadProfiles()
  }, [])

  async function loadProfiles() {
    try {
      const res = await countryHubService.listProfiles()
      setProfiles(res.data || [])
    } catch {
      // no critico
    }
  }

  async function applyProfile(profileKey) {
    try {
      setIsApplyingProfile(true)
      await countryHubService.applyProfile(profileKey, false)
      toast.success('Perfil aplicado. Personaliza los detalles y publica.')
      setProfilesDialogOpen(false)
      await load()
    } catch (e) {
      const msg = e.response?.data?.message || 'Error al aplicar perfil'
      if (
        e.response?.status === 409 &&
        (await confirmar({
          titulo: 'Ya tienes un hub armado',
          descripcion: `${msg}\n\n¿Cambias tu hub actual por este perfil? Lo que tienes ahora se reemplaza.`,
          textoConfirmar: 'Reemplazar mi hub',
          variante: 'destructiva',
        }))
      ) {
        try {
          await countryHubService.applyProfile(profileKey, true)
          toast.success('Perfil aplicado (sobreescrito)')
          setProfilesDialogOpen(false)
          await load()
        } catch (e2) {
          toast.error(e2.response?.data?.message || 'Error')
        }
      } else {
        toast.error(msg)
      }
    } finally {
      setIsApplyingProfile(false)
    }
  }

  async function checkPrevious() {
    try {
      const res = await previousHubService.checkArchive()
      if (res.data?.hasArchive) setPreviousArchive(res.data)
    } catch {
      // no critico
    }
  }

  async function applyImport() {
    try {
      setIsImporting(true)
      const res = await previousHubService.importSelective(importSelection)
      toast.success(`Importado: ${(res.data?.imported || []).join(', ') || 'nada'}`)
      setImportDialogOpen(false)
      setPreviousArchive(null)
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al importar')
    } finally {
      setIsImporting(false)
    }
  }

  async function load() {
    try {
      setIsLoading(true)
      setError(null)
      const res = await countryHubService.getCountryHub()
      const incoming = res.data?.countryHub
      setHub(incoming ? { ...emptyHub(), ...incoming } : emptyHub())
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al cargar')
    } finally {
      setIsLoading(false)
    }
  }

  async function save() {
    try {
      setIsSaving(true)
      const res = await countryHubService.updateCountryHub(hub)
      setHub({ ...emptyHub(), ...res.data })
      toast.success('Cambios guardados')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  async function togglePublish() {
    const next = !hub.isPublished
    try {
      setIsPublishing(true)
      const res = await countryHubService.publishCountryHub(next)
      setHub((prev) => ({ ...prev, ...res.data }))
      toast.success(next ? 'Publicado en el portal' : 'Despublicado')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al publicar')
    } finally {
      setIsPublishing(false)
    }
  }

  const updateField = (section, field, value) => {
    setHub((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }))
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      </PageContainer>
    )
  }

  if (partner?.organizationType !== 'country_manager') {
    return (
      <PageContainer>
        <Alert variant="warning">
          <AlertTriangle size={16} />
          <AlertTitle>Solo Country Managers</AlertTitle>
          <AlertDescription>
            Solo los Country Managers pueden gestionar el Country Hub.
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  // Status pill
  const statusBadge = hub.isPublished ? (
    <Badge variant="success" className="gap-1.5 px-3 py-1">
      <Globe size={12} /> Publicado
    </Badge>
  ) : (
    <Badge variant="secondary" className="gap-1.5 px-3 py-1">
      <GlobeLock size={12} /> Borrador
    </Badge>
  )

  // Acciones desktop (inline en PageHeader)
  const desktopActions = (
    <>
      {statusBadge}
      <Button variant="outline" size="default" asChild>
        <a href={`${publicUrl}?preview=hub`} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={16} /> Vista previa
        </a>
      </Button>
      <Button variant="default" size="default" onClick={save} disabled={isSaving}>
        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {isSaving ? 'Guardando…' : 'Guardar'}
      </Button>
      <Button
        variant={hub.isPublished ? 'outline' : 'success'}
        size="default"
        onClick={togglePublish}
        disabled={isPublishing}
      >
        {isPublishing ? (
          <Loader2 size={16} className="animate-spin" />
        ) : hub.isPublished ? (
          <GlobeLock size={16} />
        ) : (
          <Globe size={16} />
        )}
        {hub.isPublished ? 'Despublicar' : 'Publicar'}
      </Button>
    </>
  )

  return (
    <PageContainer>
      {/* Mobile-only: status badge + preview link compacto (header ya muestra título) */}
      <div className="md:hidden flex items-center justify-between gap-2 -mt-1">
        {statusBadge}
        <a
          href={`${publicUrl}?preview=hub`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-body-sm text-primary font-medium"
        >
          <ExternalLink size={14} /> Vista previa
        </a>
      </div>

      <PageHeader
        title="Tu Hub Corporativo"
        description={
          <>
            Editor del Country Hub de <strong className="text-foreground">{countryCode}</strong>.
            Después de guardar y publicar, aparece en{' '}
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium hover:underline break-all"
            >
              {publicUrl}
            </a>
          </>
        }
        actions={<div className="hidden md:flex items-center gap-2">{desktopActions}</div>}
      />

      {error && (
        <Alert variant="destructive">
          <AlertTriangle size={16} />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!hub.manager?.fullName && profiles.length > 0 && (
        <Alert variant="info">
          <Sparkles size={16} />
          <AlertTitle>Empieza rápido</AlertTitle>
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Aplica un perfil pre-poblado con KPIs, FAQ y soporte adaptados al rol de
              Country Manager. Después lo personalizas.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setProfilesDialogOpen(true)}
              className="shrink-0"
            >
              Elegir perfil
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {previousArchive && !importBannerDismissed && (
        <Alert variant="info">
          <Inbox size={16} />
          <AlertTitle>Hub heredado disponible</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>
              Heredas el país <strong>{countryCode}</strong> de{' '}
              <strong>{previousArchive.previousPartnerName}</strong>. Hay{' '}
              <strong>{previousArchive.summary?.successStories || 0}</strong> casos de éxito,{' '}
              <strong>{previousArchive.summary?.hasFaq || 0}</strong> FAQs y{' '}
              <strong>{previousArchive.summary?.futureEvents || 0}</strong> eventos futuros.
            </span>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setImportDialogOpen(true)}>
                Importar selectivamente
              </Button>
              <Button size="sm" variant="outline" onClick={() => setImportBannerDismissed(true)}>
                Empezar de cero
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs: select dropdown en mobile, tabs horizontales en desktop */}
      <Card className="overflow-hidden">
        {/* Mobile: select */}
        <div className="md:hidden border-b border-line p-3">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TABS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="hidden md:block">
          <TabsList className="px-4">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="p-4 sm:p-6 space-y-6">
          {/* TAB: Tu perfil */}
          {activeTab === 'profile' && (
            <>
              <section className="space-y-4">
                <h3 className="font-display text-h3 font-semibold text-foreground">
                  Perfil del Country Manager
                </h3>

                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                  {/* Foto profesional - círculo perfecto, fallback iniciales */}
                  <div className="shrink-0">
                    <ImageUploader
                      type="profile"
                      currentUrl={hub.manager.photoUrl}
                      height={140}
                      circular
                      fallbackName={hub.manager.fullName}
                      onUploadRequest={async (file) => {
                        const res = await countryHubService.uploadImage(file, 'profile')
                        return res.data
                      }}
                      onUpload={(data) => updateField('manager', 'photoUrl', data.url)}
                      onDelete={() => updateField('manager', 'photoUrl', '')}
                    />
                  </div>

                  <div className="flex-1 w-full grid gap-4 sm:grid-cols-2">
                    <Field label="Nombre completo" htmlFor="manager-name">
                      <Input
                        id="manager-name"
                        value={hub.manager.fullName || ''}
                        onChange={(e) => updateField('manager', 'fullName', e.target.value)}
                        placeholder="Ej: María González"
                      />
                    </Field>
                    <Field label="Cargo" htmlFor="manager-role">
                      <Input
                        id="manager-role"
                        value={hub.manager.role || ''}
                        onChange={(e) => updateField('manager', 'role', e.target.value)}
                        placeholder="Country Manager El Salvador"
                      />
                    </Field>
                    <Field
                      label="Años de experiencia"
                      htmlFor="manager-years"
                      full
                    >
                      <Input
                        id="manager-years"
                        type="number"
                        inputMode="numeric"
                        value={hub.manager.yearsExperience || ''}
                        onChange={(e) =>
                          updateField('manager', 'yearsExperience', parseInt(e.target.value) || 0)
                        }
                      />
                    </Field>
                  </div>
                </div>

                <Field label="O pega URL de foto externa" htmlFor="manager-photo-url">
                  <Input
                    id="manager-photo-url"
                    value={hub.manager.photoUrl || ''}
                    onChange={(e) => updateField('manager', 'photoUrl', e.target.value)}
                    placeholder="https://..."
                  />
                </Field>

                <Field
                  label="Bio profesional"
                  htmlFor="manager-bio"
                  helper="Tu trayectoria, especialización, visión como representante."
                >
                  <Textarea
                    id="manager-bio"
                    rows={5}
                    value={hub.manager.bio || ''}
                    onChange={(e) => updateField('manager', 'bio', e.target.value)}
                  />
                </Field>

                <FieldGroup>
                  <Field label="LinkedIn" htmlFor="manager-linkedin">
                    <Input
                      id="manager-linkedin"
                      value={hub.manager.linkedin || ''}
                      onChange={(e) => updateField('manager', 'linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </Field>
                  <Field label="Twitter / X" htmlFor="manager-twitter">
                    <Input
                      id="manager-twitter"
                      value={hub.manager.twitter || ''}
                      onChange={(e) => updateField('manager', 'twitter', e.target.value)}
                      placeholder="@usuario"
                    />
                  </Field>
                </FieldGroup>
              </section>

              <Separator />

              <section className="space-y-4">
                <h3 className="font-display text-h3 font-semibold text-foreground">
                  Tu organización
                </h3>
                <FieldGroup>
                  <Field label="Nombre" htmlFor="org-name">
                    <Input
                      id="org-name"
                      value={hub.organization.name || ''}
                      onChange={(e) => updateField('organization', 'name', e.target.value)}
                    />
                  </Field>
                  <Field label="Tagline" htmlFor="org-tagline">
                    <Input
                      id="org-tagline"
                      value={hub.organization.tagline || ''}
                      onChange={(e) => updateField('organization', 'tagline', e.target.value)}
                      placeholder="Una frase corta que define tu organización"
                    />
                  </Field>
                  <Field label="URL del logo" htmlFor="org-logo" full>
                    <Input
                      id="org-logo"
                      value={hub.organization.logoUrl || ''}
                      onChange={(e) => updateField('organization', 'logoUrl', e.target.value)}
                    />
                  </Field>
                  <Field label="Año de fundación" htmlFor="org-founded">
                    <Input
                      id="org-founded"
                      type="number"
                      inputMode="numeric"
                      value={hub.organization.foundedYear || ''}
                      onChange={(e) =>
                        updateField(
                          'organization',
                          'foundedYear',
                          parseInt(e.target.value) || null
                        )
                      }
                    />
                  </Field>
                  <Field label="Sitio web" htmlFor="org-website">
                    <Input
                      id="org-website"
                      value={hub.organization.website || ''}
                      onChange={(e) => updateField('organization', 'website', e.target.value)}
                      placeholder="https://..."
                    />
                  </Field>
                </FieldGroup>
              </section>

              <Separator />

              <RepeaterField
                label="Certificaciones"
                items={hub.manager.certifications || []}
                onChange={(items) => updateField('manager', 'certifications', items)}
                newItem={() => ({ name: '', issuer: '', year: new Date().getFullYear() })}
                max={6}
                itemLabel={(item) => item.name || 'Sin nombre'}
              >
                {(item, idx, update) => (
                  <div className="grid gap-3 sm:grid-cols-12">
                    <div className="sm:col-span-5 space-y-1.5">
                      <Label>Nombre</Label>
                      <Input
                        value={item.name || ''}
                        onChange={(e) => update('name', e.target.value)}
                      />
                    </div>
                    <div className="sm:col-span-5 space-y-1.5">
                      <Label>Emisor</Label>
                      <Input
                        value={item.issuer || ''}
                        onChange={(e) => update('issuer', e.target.value)}
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label>Año</Label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        value={item.year || ''}
                        onChange={(e) => update('year', parseInt(e.target.value) || null)}
                      />
                    </div>
                  </div>
                )}
              </RepeaterField>
            </>
          )}

          {/* TAB: KPIs */}
          {activeTab === 'kpis' && (
            <>
              <Alert variant="info">
                <CheckCircle2 size={16} />
                <AlertDescription>
                  Máximo 4 KPIs visibles. Usa métricas que muestren impacto local: número de
                  abogados, demos completadas, satisfacción, capacitaciones.
                </AlertDescription>
              </Alert>
              <RepeaterField
                label="KPIs corporativos"
                items={hub.kpis || []}
                onChange={(items) => setHub((prev) => ({ ...prev, kpis: items }))}
                newItem={() => ({ icon: 'Users', value: '', label: '', trend: '' })}
                max={4}
                itemLabel={(item) => item.label || 'Nuevo KPI'}
              >
                {(item, idx, update) => (
                  <div className="grid gap-3 sm:grid-cols-12">
                    <div className="sm:col-span-3">
                      <IconPicker
                        value={item.icon}
                        onChange={(v) => update('icon', v)}
                        label="Icono"
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label>Valor</Label>
                      <Input
                        value={item.value || ''}
                        onChange={(e) => update('value', e.target.value)}
                        placeholder="120+"
                      />
                    </div>
                    <div className="sm:col-span-4 space-y-1.5">
                      <Label>Etiqueta</Label>
                      <Input
                        value={item.label || ''}
                        onChange={(e) => update('label', e.target.value)}
                        placeholder="Abogados activos"
                      />
                    </div>
                    <div className="sm:col-span-3 space-y-1.5">
                      <Label>Tendencia</Label>
                      <Input
                        value={item.trend || ''}
                        onChange={(e) => update('trend', e.target.value)}
                        placeholder="+15% mes"
                      />
                    </div>
                  </div>
                )}
              </RepeaterField>
            </>
          )}

          {/* TAB: FAQ */}
          {activeTab === 'faq' && (
            <RepeaterField
              label="Preguntas frecuentes"
              items={hub.faq || []}
              onChange={(items) => setHub((prev) => ({ ...prev, faq: items }))}
              newItem={() => ({ question: '', answer: '', category: '' })}
              max={20}
              itemLabel={(item) => item.question || 'Nueva pregunta'}
            >
              {(item, idx, update) => (
                <div className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-12">
                    <div className="sm:col-span-9 space-y-1.5">
                      <Label>Pregunta</Label>
                      <Input
                        value={item.question || ''}
                        onChange={(e) => update('question', e.target.value)}
                      />
                    </div>
                    <div className="sm:col-span-3 space-y-1.5">
                      <Label>Categoría</Label>
                      <Input
                        value={item.category || ''}
                        onChange={(e) => update('category', e.target.value)}
                        placeholder="ej: Demos"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Respuesta</Label>
                    <Textarea
                      rows={4}
                      value={item.answer || ''}
                      onChange={(e) => update('answer', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </RepeaterField>
          )}

          {/* TAB: Soporte */}
          {activeTab === 'support' && (
            <>
              <h3 className="font-display text-h3 font-semibold text-foreground">
                Compromiso de servicio y canales
              </h3>
              <FieldGroup>
                <Field label="Email principal" htmlFor="sup-email">
                  <Input
                    id="sup-email"
                    type="email"
                    value={hub.support.primaryEmail || ''}
                    onChange={(e) => updateField('support', 'primaryEmail', e.target.value)}
                  />
                </Field>
                <Field label="Teléfono" htmlFor="sup-phone">
                  <Input
                    id="sup-phone"
                    type="tel"
                    value={hub.support.supportPhone || ''}
                    onChange={(e) => updateField('support', 'supportPhone', e.target.value)}
                  />
                </Field>
                <Field label="WhatsApp" htmlFor="sup-wa">
                  <Input
                    id="sup-wa"
                    type="tel"
                    value={hub.support.whatsapp || ''}
                    onChange={(e) => updateField('support', 'whatsapp', e.target.value)}
                    placeholder="+503 7777-8888"
                  />
                </Field>
                <Field label="SLA de respuesta" htmlFor="sup-sla">
                  <Input
                    id="sup-sla"
                    value={hub.support.responseTimeSla || ''}
                    onChange={(e) => updateField('support', 'responseTimeSla', e.target.value)}
                    placeholder="< 2 horas hábiles vía WhatsApp"
                  />
                </Field>
                <Field label="Horario de atención" htmlFor="sup-hours">
                  <Input
                    id="sup-hours"
                    value={hub.support.officeHours || ''}
                    onChange={(e) => updateField('support', 'officeHours', e.target.value)}
                    placeholder="Lun-Vie 8am-6pm CST"
                  />
                </Field>
                <Field label="Contacto de emergencia" htmlFor="sup-emergency">
                  <Input
                    id="sup-emergency"
                    value={hub.support.emergencyContact || ''}
                    onChange={(e) => updateField('support', 'emergencyContact', e.target.value)}
                  />
                </Field>
              </FieldGroup>
            </>
          )}

          {/* TAB: Branding */}
          {activeTab === 'branding' && (
            <>
              <Alert variant="info">
                <CheckCircle2 size={16} />
                <AlertDescription>
                  Tu página hereda la paleta corporativa de CoreDesk. Solo puedes ajustar el
                  color de acento y el cover image del hero.
                </AlertDescription>
              </Alert>
              <FieldGroup>
                <Field
                  label="Color de acento"
                  htmlFor="theme-color"
                  helper="Por defecto el bronce CoreDesk #B8924A"
                >
                  <div className="flex items-center gap-3">
                    <input
                      id="theme-color"
                      type="color"
                      value={hub.theme.accentColor || '#B8924A'}
                      onChange={(e) => updateField('theme', 'accentColor', e.target.value)}
                      className="h-12 w-16 rounded-md border border-line cursor-pointer bg-card"
                    />
                    <Input
                      value={hub.theme.accentColor || '#B8924A'}
                      onChange={(e) => updateField('theme', 'accentColor', e.target.value)}
                      className="font-mono uppercase"
                    />
                  </div>
                </Field>
                <Field
                  label="URL cover hero"
                  htmlFor="theme-cover-url"
                  helper="Recomendado 1920x1080 horizontal"
                >
                  <Input
                    id="theme-cover-url"
                    value={hub.theme.coverImageUrl || ''}
                    onChange={(e) => updateField('theme', 'coverImageUrl', e.target.value)}
                  />
                </Field>
              </FieldGroup>
              <div className="space-y-2">
                <Label>Cover del hero</Label>
                <ImageUploader
                  type="hero"
                  currentUrl={hub.theme.coverImageUrl}
                  height={240}
                  onUploadRequest={async (file) => {
                    const res = await countryHubService.uploadImage(file, 'hero')
                    return res.data
                  }}
                  onUpload={(data) => updateField('theme', 'coverImageUrl', data.url)}
                  onDelete={() => updateField('theme', 'coverImageUrl', '')}
                  enableUnsplash
                  unsplashCategory="legal_hero"
                />
              </div>
            </>
          )}

          {/* TAB: Footer CTA */}
          {activeTab === 'footer' && (
            <>
              <h3 className="font-display text-h3 font-semibold text-foreground">
                CTA del footer
              </h3>
              <Field label="Headline" htmlFor="footer-headline">
                <Input
                  id="footer-headline"
                  value={hub.footerCta.headline || ''}
                  onChange={(e) => updateField('footerCta', 'headline', e.target.value)}
                  placeholder="Súmate a los 120+ abogados que ya confían en CoreDesk en El Salvador"
                />
              </Field>
              <Field label="Subheadline" htmlFor="footer-sub">
                <Textarea
                  id="footer-sub"
                  rows={3}
                  value={hub.footerCta.subheadline || ''}
                  onChange={(e) => updateField('footerCta', 'subheadline', e.target.value)}
                  placeholder="Solicita una demo, conoce los planes o conecta conmigo directamente."
                />
              </Field>
            </>
          )}
        </div>
      </Card>

      {/* Mobile sticky action bar: Guardar + Publicar */}
      <StickyActionBar>
        <Button
          variant="default"
          size="lg"
          onClick={save}
          disabled={isSaving}
          className="flex-1"
        >
          {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isSaving ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button
          variant={hub.isPublished ? 'outline' : 'success'}
          size="lg"
          onClick={togglePublish}
          disabled={isPublishing}
          className="flex-1"
        >
          {isPublishing ? (
            <Loader2 size={16} className="animate-spin" />
          ) : hub.isPublished ? (
            <GlobeLock size={16} />
          ) : (
            <Globe size={16} />
          )}
          {hub.isPublished ? 'Despublicar' : 'Publicar'}
        </Button>
      </StickyActionBar>

      {/* Profiles dialog */}
      <Dialog open={profilesDialogOpen} onOpenChange={setProfilesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Elige un perfil de inicio</DialogTitle>
            <DialogDescription>
              Cada perfil prepuebla KPIs, FAQ y configuración de soporte. Tu nombre, email y
              WhatsApp se llenan automáticamente desde tu cuenta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {profiles.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyProfile(p.key)}
                disabled={isApplyingProfile}
                className="block w-full text-left rounded-lg border border-line p-3 transition-colors hover:border-primary-400 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/40 disabled:opacity-60"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{p.label}</p>
                    <p className="text-caption text-subtle line-clamp-2">
                      {p.description}
                    </p>
                  </div>
                  {p.isDefault && (
                    <Badge variant="default" className="shrink-0">
                      Recomendado
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfilesDialogOpen(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Importar contenido del manager anterior</DialogTitle>
            <DialogDescription>
              Selecciona qué aprovechar del hub anterior. Lo demás lo armas tú desde cero.
              Nada se publica automáticamente — todo entra como borrador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              {
                key: 'faq',
                label: `FAQ corporativa (${previousArchive?.summary?.hasFaq || 0} preguntas)`,
              },
              { key: 'support', label: 'Configuración de soporte (SLA, horarios, contactos)' },
              {
                key: 'successStories',
                label: `Casos de éxito (${previousArchive?.summary?.successStories || 0} casos)`,
              },
              {
                key: 'futureEvents',
                label: `Eventos futuros (${previousArchive?.summary?.futureEvents || 0})`,
              },
              { key: 'kpis', label: 'KPIs (no recomendado — empieza con tus métricas)' },
              { key: 'branding', label: 'Branding (cover image, accent color)' },
              { key: 'footerCta', label: 'Footer CTA copy' },
              {
                key: 'managerProfile',
                label: 'Perfil del manager anterior (DESACONSEJADO)',
              },
              {
                key: 'announcements',
                label: `Anuncios antiguos (${previousArchive?.summary?.announcements || 0})`,
              },
            ].map((opt) => (
              <label
                key={opt.key}
                className="flex items-start gap-3 cursor-pointer rounded-md p-2 hover:bg-surface-muted dark:hover:bg-neutral-800/60"
              >
                <Checkbox
                  checked={importSelection[opt.key]}
                  onCheckedChange={(v) =>
                    setImportSelection({ ...importSelection, [opt.key]: !!v })
                  }
                  className="mt-0.5"
                />
                <span className="text-body-sm text-foreground leading-relaxed">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={applyImport} disabled={isImporting}>
              {isImporting && <Loader2 size={16} className="animate-spin" />}
              {isImporting ? 'Importando…' : 'Importar selección'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {dialogo}
    </PageContainer>
  )
}
